/**
 * voidCallLog — soft-voids a CallLog server-side and marks the local
 * visit_task_sessions row as voided so the rep's UI updates instantly.
 * Depends on the parent visit having synced.
 *
 * EMITTED BY: StoreDetail.tsx:2199 (store_visit_abandoned). Deliberately not
 * emitted here as well — the abandon is one event, and duplicating it would
 * make a DM see the visit abandoned twice. If this op gains a second caller,
 * move the emit here and delete the screen's (#3530).
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put, getAll } from '../../utils/indexedDB';
import {
  jsonHeaders,
  classifyResponse,
  networkErrorResult,
  resolveCallLogUrl,
} from '../httpHelpers';

export interface VoidCallLogInput {
  visitLocalId: string;
  voidReason: string;
}

export interface VoidCallLogResponse {
  status: string;
  message: string;
}

export const voidCallLogOp: WriteOp<VoidCallLogInput, VoidCallLogInput, VoidCallLogResponse> = {
  kind: 'voidCallLog',

  async applyLocal(input) {
    const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
    const match = rows.find((r) => r.uuid === input.visitLocalId);
    if (!match) return;
    await put(STORES.VISIT_TASK_SESSIONS, {
      ...match,
      voided: true,
      voidReason: input.voidReason,
      status: 'voided',
    });
  },

  serialize(input, localId) {
    return {
      action: 'voidCallLog',
      endpoint: '/api/projects/calllog/{calllogid}/void/',
      method: 'PATCH',
      payload: input,
      priority: 40,
      maxRetries: 10,
      dependsOn: input.visitLocalId,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as VoidCallLogInput;
    const urlOrShortCircuit = await resolveCallLogUrl(row, input.visitLocalId);
    if (typeof urlOrShortCircuit !== 'string') return urlOrShortCircuit;
    const url = urlOrShortCircuit;

    let response: Response;
    try {
      response = await fetch(url, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify({ void_reason: input.voidReason }),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;
    return { status: 'success', retriable: false };
  },

  async reconcile() {},

  dependsOn(input) {
    return input.visitLocalId;
  },
};

export type VoidCallLogOp = typeof voidCallLogOp;
